import { useEffect, useState } from 'react'
import { FormGroup, Label, Modal, ModalBody, ModalFooter } from 'reactstrap'
import { Formik } from 'formik'
import { CustomButton } from 'modules/auth/styles'
import UserMultiSelect from 'modules/app/components/UserMultiSelect'
import { ReactComponent as CloseIcon } from 'assets/images/icons/close.svg'
import { ButtonLoader, getDefaultSharingOnEdit } from 'utils/storageUtils'
import { ModalForm } from 'modules/app/styles'
import { connect } from 'react-redux'
import { getAllTeams, getCompanyPermissionObject, getTeamPermissionObject, getTitle, handleApiError, sharing_access } from 'utils/helpers'
import { getDrafts_action, toggleContentPermissionModal_action } from 'store/actions'
import { contentSharing_api, getContentByUUID_api, getDraftsById_api } from 'store/apis'
import { toast } from 'react-toastify'

export function DraftPermissionsModal(props) {
  const {
    contents: { editingContentPermission, showContentPermissionModal },
    company,
    teams,
    user
  } = props
  const [options, set_options] = useState([])
  const [is_submitting, set_is_submitting] = useState(false)
  const [saved, set_saved] = useState(false)
  const [is_loading, set_is_loading] = useState(false)
  const [content, set_content] = useState({})

  const getContent = async (editingContentPermission) => {
    try {
      set_is_loading(true)
      let data
      if (editingContentPermission.type === 'page' && editingContentPermission.is_draft) {
        data = await getDraftsById_api(editingContentPermission.uuid)
      } else {
        data = await getContentByUUID_api(editingContentPermission.uuid)
      }
      if (data.id) {
        set_content(data)
      }
    } catch (error) {
      handleApiError(error)
    }
    set_is_loading(false)
  }
  useEffect(() => {
    if (editingContentPermission.id) getContent(editingContentPermission)
  }, [editingContentPermission])

  // Company options:
  useEffect(() => {
    if (!company.loading && company.company.id) {
      set_options((data) => [...data, getCompanyPermissionObject(company.company)])
    }
  }, [company.loading, company.company])

  // Team options:
  useEffect(() => {
    const all_teams = getAllTeams(teams)
    set_options((data) => [...data, ...getTeamPermissionObject(all_teams)])
  }, [teams])

  return (
    <Modal zIndex={props.fullScreenStatus ? '300000000000' : ''} centered fade={false} isOpen={showContentPermissionModal}>
      {!is_loading && (
        <Formik
          enableReinitialize
          initialValues={{
            id: content.id || null,
            owners: [
              { ...content.user, user_id: content.user_id, value: content.user ? content.user.member_code : '', option_type: 'user', isFixed: true },
              ...getDefaultSharingOnEdit(sharing_access.owner, content)
            ],
            editors: getDefaultSharingOnEdit(sharing_access.editor, content),
            viewers: getDefaultSharingOnEdit(sharing_access.viewer, content)
          }}
          onSubmit={async (values) => {
            try {
              set_is_submitting(true)

              // Update sharing
              const owners = values.owners.map((u) => {
                u.access = sharing_access.owner
                return u
              })
              const editors = values.editors.map((u) => {
                u.access = sharing_access.editor
                return u
              })
              const viewers = values.viewers.map((u) => {
                u.access = sharing_access.viewer
                return u
              })
              const sharings = [...owners, ...editors, ...viewers]
              if (sharings.length === 0) {
                // eslint-disable-next-line no-throw-literal
                throw { _errors: ['Select owners or viewers.'] }
              }
              const shared_everyone = JSON.stringify(sharings.filter((c) => c.option_type === 'company').map((c) => ({ access: c.access })))
              const shared_teams = JSON.stringify(sharings.filter((c) => c.option_type === 'team').map((c) => ({ id: c.team_id, access: c.access })))
              const shared_users = JSON.stringify(sharings.filter((c) => c.option_type === 'user').map((c) => ({ id: c.user_id, access: c.access })))

              const data = {
                content_id: editingContentPermission.id,
                shared_everyone,
                shared_teams,
                shared_users
              }
              const result = await contentSharing_api(data)
              toast.success(result.message)
              props.getDrafts()
              set_saved(true)
              setTimeout(() => {
                set_saved(false)
                props.toggleContentPermissionModal(false)
              }, 2000)
            } catch (error) {
              handleApiError(error)
            }
            set_is_submitting(false)
          }}>
          {(formikProps) => (
            <ModalForm onSubmit={formikProps.handleSubmit} autoComplete='off'>
              <ModalBody>
                <a
                  href='/'
                  className='modal-close'
                  onClick={(e) => {
                    e.preventDefault()
                    props.toggleContentPermissionModal(false)
                  }}>
                  <CloseIcon />
                </a>
                <div className='title'> {editingContentPermission.type} Permissions </div>

                <FormGroup className='mb-5'>
                  <Label>Title</Label>
                  <h4 dir='auto'>{getTitle(content.title)}</h4>
                </FormGroup>

                <FormGroup className='mb-5'>
                  <Label>Owners: </Label>
                  <UserMultiSelect
                    name='owners'
                    options={options}
                    defaultValue={formikProps.values.owners}
                    onChange={(value) => formikProps.setFieldValue('owners', value)}
                    user={user}
                    hidden={[...formikProps.values.viewers, ...formikProps.values.editors]}
                  />
                  <div className='text-muted'>
                    <small>Owners can edit, publish, share and delete a {content.type} </small>
                  </div>
                </FormGroup>
                <FormGroup className='mb-5'>
                  <Label>Editors: </Label>
                  <UserMultiSelect
                    name='editors'
                    options={options}
                    defaultValue={formikProps.values.editors}
                    onChange={(value) => formikProps.setFieldValue('editors', value)}
                    user={user}
                    hidden={[...formikProps.values.viewers, ...formikProps.values.owners]}
                  />
                  <div className='text-muted'>
                    <small>Editors can edit and publish a {content.type}</small>
                  </div>
                </FormGroup>
                <FormGroup className='mb-5'>
                  <Label>Viewers: </Label>
                  <UserMultiSelect
                    name='viewers'
                    options={options}
                    defaultValue={formikProps.values.viewers}
                    onChange={(value) => formikProps.setFieldValue('viewers', value)}
                    user={user}
                    hidden={formikProps.values.owners}
                  />
                  <div className='text-muted'>
                    <small>Viewers can comment on and view a {content.type}</small>
                  </div>
                </FormGroup>
                <div className='px-2 py-2 light-blue-100-opacity text-center mr-auto'>Admins have the same access as owners always</div>
              </ModalBody>
              <ModalFooter className='d-flex justify-content-center'>
                <CustomButton type='submit' className={`mx-5 ${!saved ? 'black' : 'green'}`} disabled={is_submitting || saved}>
                  Update Permissions {is_submitting && <ButtonLoader />}
                </CustomButton>
                <a
                  href='/'
                  className='text-dark font-weight-600'
                  onClick={(e) => {
                    e.preventDefault()
                    props.toggleContentPermissionModal(false)
                  }}>
                  Cancel
                </a>
              </ModalFooter>
            </ModalForm>
          )}
        </Formik>
      )}
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  contents: state.contents,
  company: state.company,
  teams: state.teams,
  fullScreenStatus: state.contents.isFullScreen
})

const mapDispatchToProps = (dispatch) => ({
  toggleContentPermissionModal: (status) => dispatch(toggleContentPermissionModal_action(status)),
  getDrafts: () => dispatch(getDrafts_action())
})
export default connect(mapStateToProps, mapDispatchToProps)(DraftPermissionsModal)
